import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { NURSE_ROLE } from '../constants/nurseScope';

export interface NurseScope {
  /** True only when the user's active custom role is `nurse`. */
  isNurse: boolean;
  /** Patient 360 header — Order ▾ (one-time order, IV event). */
  canCreateOrders: boolean;
  /** Patient 360 header — Subscription ▾ (create / add drug / switch …). */
  canCreateSubscriptions: boolean;
  /** Subscription Overview card — "Edit drug" / "Recalc follow-up". */
  canEditSubscriptions: boolean;
  /** Delivery Status → Clinic Shipments — inline notes, resync, receive. */
  canEditClinicShipments: boolean;
}

/**
 * Nurse-role capability gates (GP-1183 / GP-211).
 *
 * Mirrors the graceful-fallback contract used everywhere else in PHOX: while
 * the custom role is still loading, or when the user has no custom role at all,
 * nothing is restricted (`isNurse` is false). Only an explicitly resolved
 * `nurse` role narrows the UI. Route/nav scoping lives in
 * `constants/nurseScope.ts`.
 */
export const useNurseScope = (): NurseScope => {
  const { customRole, hasCustomRole } = useAuth();
  const isNurse = customRole ? hasCustomRole(NURSE_ROLE) : false;

  return useMemo(
    () => ({
      isNurse,
      canCreateOrders: !isNurse,
      canCreateSubscriptions: !isNurse,
      canEditSubscriptions: !isNurse,
      canEditClinicShipments: !isNurse,
    }),
    [isNurse],
  );
};
